import React from "react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList,
} from "recharts";
import { T, SERIES_ORDER, statusFill } from "../theme/tokens.js";
import { Empty } from "./ui/index.jsx";
import { buildView } from "../lib/trackerViews.js";
import { fmtMoney } from "../lib/format.js";

/* Drawing a saved view.
 *
 * The series colours come from the validated set in tokens, taken in their
 * fixed order, because that order was chosen so no two adjacent entries fail
 * separation for a colourblind reader. Where a view has more series than the
 * set has colours it cycles, which is honest: past four or five series a
 * chart is a table with decoration, and the table view is right there.
 */

const axis = { tick: { fontSize: 11, fill: T.soft }, stroke: T.line };
const grid = { strokeDasharray: "3 3", stroke: T.line };

/* Colours for a set of names.
 *
 * A chart split by a status column takes the status colours, so a Complete
 * band here is the same green as a Complete chip on the register. That only
 * holds when every name resolves and no two land on the same colour, because
 * two identical bands are worse than an arbitrary pair. Otherwise the fixed
 * categorical order, which is the only order whose adjacencies were validated.
 */
function colorsFor(names) {
  const mapped = names.map(statusFill);
  if (mapped.every(Boolean) && new Set(mapped).size === names.length) return mapped;
  return names.map((_, i) => SERIES_ORDER[i % SERIES_ORDER.length]);
}

const tooltip = {
  contentStyle: {
    background: "#fff", border: `1px solid ${T.line}`, borderRadius: 4,
    fontSize: 12.5, color: T.ink, boxShadow: "0 4px 14px rgba(20,40,75,0.14)",
  },
  labelStyle: { color: T.navy, fontWeight: 700 },
};

/* Money reads as money. A sum of a column called Amount is dollars in every
   one of these workbooks, and $3,699,477 is legible where 3699476.84 is not. */
const looksLikeMoney = (label = "") => /amount|dollar|cost|value|price|\$/i.test(label);
const fmtValue = (v, label) => (looksLikeMoney(label)
  ? fmtMoney(v)
  : (Math.round(v * 100) / 100).toLocaleString());

function ViewTable({ model, onOpen }) {
  return (
    <div style={{ overflowX: "auto", maxHeight: 340, overflowY: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr>
            {[model.groupLabel, ...model.series].map((h, i) => (
              <th key={h} style={{
                position: "sticky", top: 0, background: T.panel,
                textAlign: i ? "right" : "left", padding: "7px 10px",
                borderBottom: `2px solid ${T.navy}`, color: T.navy, fontSize: 11,
                fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
                whiteSpace: "nowrap",
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {model.data.map((row) => (
            <tr key={row._key}
              onClick={onOpen ? () => onOpen(row.group, row._allRows) : undefined}
              style={{ borderBottom: `1px solid ${T.line}`,
                       cursor: onOpen ? "pointer" : "default" }}>
              <td style={{ padding: "6px 10px", overflowWrap: "anywhere" }}>{row.group}</td>
              {model.series.map((s) => (
                <td key={s} style={{ padding: "6px 10px", textAlign: "right", whiteSpace: "nowrap" }}>
                  {fmtValue(row[s], model.measureLabel)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* Opening a mark.
 *
 * Every mark here knows the rows it was drawn from, so the click just hands
 * them up. A bar opens its own segment; a point on a line opens everything at
 * that date, because on a stacked trend the segment under the cursor is a
 * guess and the date is not. */
export default function TrackerChart({ spec, columns, rows, height, onDrill }) {
  const model = buildView(spec, columns, rows);

  if (model.error) return <Empty text={model.error} />;

  const drill = (title, records) => {
    if (onDrill && records?.length) onDrill({ title, rows: records });
  };
  const openable = Boolean(onDrill);

  if (model.kind === "kpi") {
    return (
      <div style={{ padding: "10px 4px", cursor: openable ? "pointer" : "default" }}
        onClick={() => drill(model.measureLabel, model.rows)}>
        <div style={{ fontSize: 40, fontWeight: 700, color: T.navy, lineHeight: 1.1 }}>
          {fmtValue(model.value, model.measureLabel)}
        </div>
        <div style={{ fontSize: 13, color: T.soft, marginTop: 4 }}>
          {model.measureLabel} across {model.rowCount.toLocaleString()} rows
        </div>
      </div>
    );
  }

  if (!model.data.length) return <Empty text="Nothing to chart from these rows." />;
  if (model.kind === "table") return <ViewTable model={model} onOpen={drill} />;

  const capped = model.seriesCapped > 0 && (
    <div style={{ fontSize: 12, color: T.soft, marginTop: 8 }}>
      The {model.seriesCapped} smallest of these are gathered into "Everything
      else". Splitting by something with fewer values, or the table view, shows
      them separately.
    </div>
  );

  const h = height ?? Math.max(240, Math.min(460, model.data.length * 30 + 80));
  const stacked = model.series.length > 1;
  const seriesColors = colorsFor(model.series);
  const colorAt = (i) => seriesColors[i % seriesColors.length];

  if (model.kind === "donut") {
    const slices = model.data.map((d) => ({ name: d.group, value: d._total, rows: d._allRows }));
    /* A donut's slices are the groups, not the series, so they are coloured
       from their own names. */
    const sliceColors = colorsFor(slices.map((s) => s.name));
    return (
      <><ResponsiveContainer width="100%" height={h}>
        <PieChart>
          <Pie data={slices} dataKey="value" nameKey="name" innerRadius="52%" outerRadius="80%"
            paddingAngle={1} cursor={openable ? "pointer" : undefined}
            onClick={(s) => drill(s.name, s.rows)}>
            {slices.map((s, i) => (
              <Cell key={s.name} fill={sliceColors[i % sliceColors.length]}
                stroke="#fff" strokeWidth={2} />
            ))}
          </Pie>
          <Tooltip {...tooltip} formatter={(v) => fmtValue(v, model.measureLabel)} />
          <Legend wrapperStyle={{ fontSize: 12, color: T.soft }} />
        </PieChart>
      </ResponsiveContainer>{capped}</>
    );
  }

  if (model.kind === "line") {
    return (
      <><ResponsiveContainer width="100%" height={h}>
        <LineChart data={model.data} margin={{ left: 8, right: 20, top: 10 }}
          onClick={(state) => {
            const d = state?.activePayload?.[0]?.payload;
            if (d) drill(d.group, d._allRows);
          }}>
          <CartesianGrid {...grid} vertical={false} />
          <XAxis dataKey="group" {...axis} />
          <YAxis {...axis} width={70}
            tickFormatter={(v) => fmtValue(v, model.measureLabel)} />
          <Tooltip {...tooltip} formatter={(v) => fmtValue(v, model.measureLabel)} />
          {stacked && <Legend wrapperStyle={{ fontSize: 12, color: T.soft }} />}
          {model.series.map((s, i) => (
            <Line key={s} type="monotone" dataKey={s} stroke={colorAt(i)} strokeWidth={2.5}
              dot={{ r: 3 }} activeDot={{ r: 5 }} />
          ))}
        </LineChart>
      </ResponsiveContainer>{capped}</>
    );
  }

  /* Bars lie down, columns stand up. Same chart, and long category names are
     readable one way and not the other. */
  const vertical = model.kind === "bar";
  return (
    <><ResponsiveContainer width="100%" height={h}>
      <BarChart data={model.data} layout={vertical ? "vertical" : "horizontal"}
        margin={{ left: 8, right: 24, top: 10 }}>
        <CartesianGrid {...grid} vertical={!vertical} horizontal={vertical} />
        {vertical ? (
          <>
            <XAxis type="number" {...axis} tickFormatter={(v) => fmtValue(v, model.measureLabel)} />
            <YAxis type="category" dataKey="group" width={150} {...axis} />
          </>
        ) : (
          <>
            <XAxis dataKey="group" {...axis} />
            <YAxis {...axis} width={70} tickFormatter={(v) => fmtValue(v, model.measureLabel)} />
          </>
        )}
        <Tooltip {...tooltip} cursor={{ fill: T.line + "44" }}
          formatter={(v) => fmtValue(v, model.measureLabel)} />
        {stacked && <Legend wrapperStyle={{ fontSize: 12, color: T.soft }} />}
        {model.series.map((s, i) => (
          <Bar key={s} dataKey={s} stackId={stacked ? "a" : undefined} fill={colorAt(i)}
            cursor={openable ? "pointer" : undefined}
            onClick={(d) => drill(stacked ? `${d.group}: ${s}` : d.group,
              stacked ? d._rows?.[s] : d._allRows)}
            radius={vertical ? [0, 3, 3, 0] : [3, 3, 0, 0]}>
            {!stacked && (
              /* The label must not swallow a click meant for its bar. */
              <LabelList dataKey={s} position={vertical ? "right" : "top"}
                style={{ fill: T.soft, fontSize: 11, pointerEvents: "none" }}
                formatter={(v) => fmtValue(v, model.measureLabel)} />
            )}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>{capped}</>
  );
}
